/* Keep shop-wide Trustpilot counts exact, including legacy translated badge copy.
 * This runs independently of lazy review carousels and never touches customer totals.
 */
(() => {
  const countSelector = '[data-mtp-live-count], [data-mhv2-live-count], [data-mr-live-count]';
  // `.nb-drawer-reviews` is the upsell drawer's caption ("4.5 van 5000+ reviews"). It is server-rendered
  // from a merchant text setting and hidden with CSS until the drawer opens, so it is present in the DOM
  // at load and needs no separate hook for the open event.
  const textSelector =
    '.maison-trustpilot__text, .maison-hpv2__rating-caption, .maison-reviews__rating-text, .nb-drawer-reviews';
  let request;

  function loadCount() {
    if (!request) {
      request = fetch('https://samako-trustpilot.maison.dev/api/v1/global-reviews?page=1&limit=1', {
        headers: { Accept: 'application/json' }, credentials: 'omit'
      }).then(response => {
        if (!response.ok) throw new Error('Trustpilot HTTP ' + response.status);
        return response.json();
      }).then(payload => {
        const total = payload.business?.reviewCounts?.total;
        if (!Number.isSafeInteger(total) || total < 0) throw new Error('Invalid Trustpilot count');
        // The drawer caption leads with the score ("4.5 van ..."), so the live score is read too. It is
        // optional: a missing or out-of-range value leaves the configured score untouched rather than
        // failing the whole sync, which would also drop the count everywhere else on the page.
        const score = payload.business?.trustScore;
        const validScore = typeof score === 'number' && score > 0 && score <= 5 ? score : null;
        return { total, score: validScore };
      }).catch(error => {
        request = null;
        throw error;
      });
    }
    return request;
  }

  function sync() {
    if (!document.querySelector(countSelector + ', ' + textSelector)) return;
    loadCount().then(({ total, score }) => {
      const lang = document.documentElement.lang || undefined;
      let formatted;
      try { formatted = total.toLocaleString(lang); }
      catch (_) { formatted = String(total); }

      // One decimal, localised — NL renders "4,4" where EN renders "4.4", matching how the merchant
      // wrote the caption by hand.
      let formattedScore = null;
      if (score !== null) {
        try {
          formattedScore = score.toLocaleString(lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        } catch (_) {
          formattedScore = score.toFixed(1);
        }
      }

      document.querySelectorAll(countSelector).forEach(element => {
        // Product-review aggregates belong to their own API, not the business total.
        if (element.closest('.maison-reviews')?.querySelector('[data-kind="product"]')) return;
        element.textContent = formatted;
        const suffix = element.nextSibling;
        if (suffix?.nodeType === Node.TEXT_NODE) suffix.textContent = suffix.textContent.replace(/^\s*\+/, '');
      });

      document.querySelectorAll(textSelector).forEach(element => {
        if (element.closest('.maison-reviews')?.querySelector('[data-kind="product"]')) return;
        const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
        const nodes = [];
        while (walker.nextNode()) nodes.push(walker.currentNode);
        nodes.forEach(node => {
          if (node.parentElement.closest(countSelector)) return;
          // Existing NL/EN/DE translations can contain literal counts even when
          // the source setting uses a token. Match review counts only, not ratings
          // or the separate "200.000+ happy customers" claim in the same badge.
          node.textContent = node.textContent.replace(
            /\b\d[\d.,\u00a0\u202f]*\+?(?=\s+(?:(?:echte|real|verified|geverifieerde)\s+)?(?:reviews|ervaringen|experiences|bewertungen|rezensionen)\b)/gi,
            formatted
          );

          // Leading score, e.g. "4.5 van 5000+ reviews" \u2192 "4,4 van 5.094 reviews". Anchored to the start
          // and required to be followed by a separator word, so it can only ever match the score at the
          // head of the caption \u2014 never the count that the rule above just wrote, and never a bare number
          // in unrelated copy. Runs second because the count replacement can change the string's tail.
          if (formattedScore !== null) {
            node.textContent = node.textContent.replace(
              /^(\s*)\d+(?:[.,]\d+)?(?=\s+(?:van|out of|from|uit|von|of)\b)/i,
              '$1' + formattedScore
            );
          }
        });
      });
    }).catch(() => { /* Keep the configured fallback when the service is unavailable. */ });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync, { once: true });
  else sync();
  document.addEventListener('shopify:section:load', sync);
})();
